import { Box, Stack } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { IPartFinderAttributeValue } from 'companySettings/types';
import { Button, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { Direction, Variant } from 'theme/enums';

import { VALUE_MAX_LENGTH } from './constants';

interface IProps {
  onAdd: (value: IPartFinderAttributeValue) => boolean;
}

export const ValuesEditor = ({ onAdd }: IProps) => {
  const { t } = useTranslation();

  const { control, errors, getValues, setValue, setError, clearErrors } = useFormContext();

  const onAddValue = () => {
    const value = (getValues('newValue') ?? '').trim();

    if (!value) {
      return;
    }

    if (value.length > VALUE_MAX_LENGTH) {
      setError('newValue', {
        type: 'maxLength',
        message: t('errorFieldMaxLength', { field: t('labelValue'), length: VALUE_MAX_LENGTH }),
      });

      return;
    }

    if (!onAdd({ value })) {
      setError('newValue', { type: 'validate', message: t('errorPartFinderAttributeValueExists') });

      return;
    }

    clearErrors('newValue');
    setValue('newValue', '');
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if ('Enter' !== event.key) {
      return;
    }

    event.preventDefault();
    onAddValue();
  };

  return (
    <Stack direction={Direction.ROW} gap={1} alignItems="flex-end">
      <Box sx={{ flexGrow: 1 }}>
        <FormField
          name="newValue"
          type={InputType.TEXT_INPUT}
          inputProps={{
            control,
            error: errors?.newValue,
            showFieldError: true,
            label: t('labelAddValue'),
            placeholder: t('placeholderValueName'),
            onKeyDown,
          }}
        />
      </Box>
      <Button
        text={t('buttonLabelAdd')}
        variant={Variant.SOFT}
        startDecorator={<Plus />}
        onClick={onAddValue}
      />
    </Stack>
  );
};
